/**
 * Resource Hook Factory
 *
 * Creates hooks for resource management with immediate API sync.
 * All operations (create, update, delete) are pushed to the server immediately.
 * No local state - the server is the single source of truth.
 */

import { useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ResourcesResult, ResourceConfig, BaseResource } from "./types";
import { optimisticListMutation, urlPredicate } from "@/utils/optimistic";
import logger from "@/utils/logger";

/**
 * Create a resource hook with immediate API sync.
 *
 * @param config - Configuration for the resource hook
 * @returns A custom hook for managing resources
 *
 * @example
 * ```ts
 * const useDashboards = createResourceHook({
 *   resourceName: 'dashboard',
 *   useQueryHook: useDashboardsQuery,
 *   useCreateMutation: useCreateDashboard,
 *   useUpdateMutation: useUpdateDashboard,
 *   useDeleteMutation: useDeleteDashboard,
 * });
 * ```
 */
export function createResourceHook<T extends BaseResource, TCreate = Partial<T>>(
  config: ResourceConfig<T, TCreate>
) {
  const {
    resourceName,
    resourcePath,
    useQueryHook,
    useCreateMutation,
    useUpdateMutation,
    useDeleteMutation,
  } = config;

  const predicate = resourcePath ? urlPredicate(resourcePath) : null;

  // Return the hook
  return function useResources(): ResourcesResult<T, TCreate> {
    const queryClient = useQueryClient();

    // Server data
    const { data: serverData, isLoading, error, refetch } = useQueryHook();

    // Mutations
    const createMutation = useCreateMutation();
    const updateMutation = useUpdateMutation();
    const deleteMutation = useDeleteMutation();

    // Operation states
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // ========================================================================
    // Resources
    // ========================================================================

    const resources = useMemo(() => {
      return serverData ?? [];
    }, [serverData]);

    // ========================================================================
    // CRUD Actions (immediate API calls)
    // ========================================================================

    /**
     * Create a new resource on the server.
     */
    const create = useCallback(
      async (data: TCreate): Promise<T> => {
        setIsCreating(true);
        logger.debug(`[${resourceName}] Creating`, data);

        try {
          const created = await createMutation.mutateAsync(data);

          // Refetch to include the new resource
          refetch();

          logger.info(`[${resourceName}] Created`, { id: created.id });
          return created;
        } finally {
          setIsCreating(false);
        }
      },
      [createMutation, refetch]
    );

    /**
     * Update a resource on the server immediately.
     * When resourcePath is configured, applies an optimistic cache update first.
     */
    const update = useCallback(
      async (id: string, changes: Partial<T>): Promise<T> => {
        setIsUpdating(true);
        logger.debug(`[${resourceName}] Updating`, { id, changes });

        try {
          if (predicate) {
            let result!: T;
            await optimisticListMutation<T>({
              queryClient,
              queryPredicate: predicate,
              operation: { type: "update", id, changes },
              mutationFn: async () => {
                result = await updateMutation.mutateAsync({
                  uriParams: { id },
                  ...changes,
                } as { uriParams: { id: string } } & Record<string, unknown>);
              },
              onSettled: () => refetch(),
            });
            logger.info(`[${resourceName}] Updated`, { id });
            return result;
          }

          const updated = await updateMutation.mutateAsync({
            uriParams: { id },
            ...changes,
          } as { uriParams: { id: string } } & Record<string, unknown>);
          refetch();
          logger.info(`[${resourceName}] Updated`, { id });
          return updated;
        } finally {
          setIsUpdating(false);
        }
      },
      [queryClient, updateMutation, refetch],
    );

    /**
     * Delete a resource from the server immediately.
     * When resourcePath is configured, removes the item from cache optimistically.
     */
    const remove = useCallback(
      async (id: string): Promise<void> => {
        setIsDeleting(true);
        logger.debug(`[${resourceName}] Deleting`, { id });

        try {
          if (predicate) {
            await optimisticListMutation<T>({
              queryClient,
              queryPredicate: predicate,
              operation: { type: "remove", id },
              mutationFn: () => deleteMutation.mutateAsync({ uriParams: { id } }),
              onSettled: () => refetch(),
            });
            logger.info(`[${resourceName}] Deleted`, { id });
            return;
          }

          await deleteMutation.mutateAsync({ uriParams: { id } });
          refetch();
          logger.info(`[${resourceName}] Deleted`, { id });
        } finally {
          setIsDeleting(false);
        }
      },
      [queryClient, deleteMutation, refetch],
    );

    /**
     * Refresh data from server.
     */
    const refresh = useCallback(() => {
      refetch();
    }, [refetch]);

    // ========================================================================
    // Return
    // ========================================================================

    return {
      // Data
      resources,
      isLoading,
      error: error as Error | null,

      // CRUD Actions
      create,
      update,
      remove,

      // Operation states
      isCreating,
      isUpdating,
      isDeleting,

      // Utilities
      refresh,
      createMutation,
    };
  };
}

// Re-export types
export * from "./types";
